import { appendCeoAccessLog } from './accessLogsMock';
import { CEO_SESSION_STORAGE_KEY, CEO_PIN_ATTEMPT_STATE_KEY } from './constants';
import { CEO_ANALYTICS_SECURITY_CONFIG } from './securityMock';
import type {
  CeoAccessLogEntry,
  CeoAccessLogSource,
  CeoAccessLogStatus,
  CeoPinAttemptState,
  CeoPinSubmitResult,
} from './types';

const DEFAULT_ATTEMPT_STATE: CeoPinAttemptState = {
  failedAttempts: 0,
  lockedUntil: null,
};

function readAttemptState(): CeoPinAttemptState {
  try {
    const raw = sessionStorage.getItem(CEO_PIN_ATTEMPT_STATE_KEY);
    if (!raw) return { ...DEFAULT_ATTEMPT_STATE };
    return JSON.parse(raw) as CeoPinAttemptState;
  } catch {
    return { ...DEFAULT_ATTEMPT_STATE };
  }
}

function writeAttemptState(state: CeoPinAttemptState): void {
  sessionStorage.setItem(CEO_PIN_ATTEMPT_STATE_KEY, JSON.stringify(state));
}

export function isCeoAuthenticated(): boolean {
  try {
    return sessionStorage.getItem(CEO_SESSION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function grantCeoAccess(): void {
  sessionStorage.setItem(CEO_SESSION_STORAGE_KEY, 'true');
}

export function revokeCeoAccess(): void {
  sessionStorage.removeItem(CEO_SESSION_STORAGE_KEY);
}

/** Проверка PIN по моковым настройкам: серверной ручки нет. */
export function validateCeoPin(pin: string): boolean {
  return pin === CEO_ANALYTICS_SECURITY_CONFIG.CEO_ANALYTICS_PIN;
}

export function createCeoAccessLog(
  status: CeoAccessLogStatus,
  reason: string,
  source: CeoAccessLogSource = 'Dashboard',
): CeoAccessLogEntry {
  return appendCeoAccessLog(status, reason, source);
}

export function isCeoPinLocked(): boolean {
  const state = readAttemptState();
  if (!state.lockedUntil) return false;

  if (Date.now() >= state.lockedUntil) {
    writeAttemptState({ ...DEFAULT_ATTEMPT_STATE });
    return false;
  }

  return true;
}

export function getCeoPinLockRemainingMs(): number {
  const state = readAttemptState();
  if (!state.lockedUntil) return 0;
  return Math.max(0, state.lockedUntil - Date.now());
}

export function clearCeoPinAttempts(): void {
  writeAttemptState({ ...DEFAULT_ATTEMPT_STATE });
}

function lockPinAttempts(source: CeoAccessLogSource): void {
  const lockedUntil =
    Date.now() + CEO_ANALYTICS_SECURITY_CONFIG.lockTimeMinutes * 60 * 1000;

  writeAttemptState({
    failedAttempts: CEO_ANALYTICS_SECURITY_CONFIG.maxAttempts,
    lockedUntil,
  });

  createCeoAccessLog('locked', 'Превышено число попыток', source);
}

/**
 * Validates PIN, tracks attempts, writes access logs.
 * Отдаёт результат интерфейсу; настоящей проверки на сервере пока нет.
 */
export function submitCeoPin(
  pin: string,
  source: CeoAccessLogSource = 'Dashboard',
): CeoPinSubmitResult {
  if (isCeoPinLocked()) {
    return { ok: false, error: 'locked', locked: true };
  }

  if (pin.length < 4) {
    return { ok: false, error: 'incomplete' };
  }

  if (validateCeoPin(pin)) {
    clearCeoPinAttempts();
    createCeoAccessLog('success', 'PIN подтверждён', source);
    return { ok: true };
  }

  const state = readAttemptState();
  const nextAttempts = state.failedAttempts + 1;
  const attemptsLeft = CEO_ANALYTICS_SECURITY_CONFIG.maxAttempts - nextAttempts;

  createCeoAccessLog('failed', 'Неверный PIN', source);

  if (nextAttempts >= CEO_ANALYTICS_SECURITY_CONFIG.maxAttempts) {
    lockPinAttempts(source);
    return { ok: false, error: 'locked', locked: true };
  }

  writeAttemptState({
    failedAttempts: nextAttempts,
    lockedUntil: null,
  });

  return {
    ok: false,
    error: 'invalid_pin',
    attemptsLeft,
  };
}
